'use client';

import React from 'react';

type Props = { children: React.ReactNode; label?: string };
type State = { error: Error | null };

/**
 * Contains render-time crashes from a chart (Nivo) so a broken graph can never take down the whole
 * page. Nivo throws hard on some internal states (e.g. "Unable to find the theme, did you forget to
 * wrap your component with ThemeProvider?") which, uncaught, surfaces as a fatal overlay. Here we
 * catch it, log the real error to the console for debugging, and render an inline fallback showing
 * the message in place of the chart.
 */
export class ChartErrorBoundary extends React.Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: unknown, info: React.ErrorInfo) {
    const message = error instanceof Error ? error.message : String(error);
    console.error(`[ChartErrorBoundary]${this.props.label ? ` ${this.props.label}:` : ''} chart render crashed:`, message, error, info.componentStack);
  }

  private reset = () => this.setState({ error: null });

  render() {
    if (this.state.error) {
      return (
        <div className="flex h-full min-h-[160px] w-full flex-col items-center justify-center gap-2 rounded-lg border border-[#2A2A2A] bg-[#141414] p-4 text-center">
          <span className="text-xs text-white/50">{this.props.label ?? 'Chart'} failed to render.</span>
          <span className="max-w-full truncate font-mono text-[10px] text-white/30" title={this.state.error.message}>
            {this.state.error.message}
          </span>
          <button
            type="button"
            onClick={this.reset}
            className="mt-1 rounded-md border border-white/10 bg-white/[0.04] px-2 py-1 text-[11px] text-white/60 transition-colors hover:bg-white/[0.08] hover:text-white/85"
          >
            Retry
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}
